import { StandardEvents } from '@shopify/events';

class CgWizardCuccioliMammeEngine extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.wizard = this.closest('cg-feeding-wizard');
    if (!this.wizard || this.wizard.dataset.calculatorType !== 'cuccioli_mamme') return;

    this.ageButtons = [...this.wizard.querySelectorAll('[data-growth-age-option]')];
    this.weightButtons = [...this.wizard.querySelectorAll('[data-growth-weight-option]')];
    this.variants = this.parseVariants();
    this.syncVariant(this.dataset.selectedVariantId || this.wizard.dataset.selectedVariantId);
    this.ageButtons.forEach((button) => {
      button.addEventListener('click', () => this.updateAvailableWeights(button.dataset.wizardValue), { signal });
    });
    this.wizard.addEventListener('cg:wizard:calculate', this.handleCalculate, { signal });
    document.addEventListener(StandardEvents.productSelect, this.handleProductSelect, { signal });
    this.updateAvailableWeights();
  }

  disconnectedCallback() {
    this.controller?.abort();
  }

  parseVariants() {
    const script = this.wizard.querySelector('[data-wizard-variants]');
    if (!script?.textContent) return new Map();
    try {
      const variants = JSON.parse(script.textContent);
      return Array.isArray(variants) ? new Map(variants.map((variant) => [String(variant.id), variant])) : new Map();
    } catch (error) {
      console.error('[CG Wizard Cuccioli e Mamme] Impossibile leggere le varianti.', error);
      return new Map();
    }
  }

  updateAvailableWeights(age) {
    let invalidated = false;
    this.weightButtons.forEach((button) => {
      const compatible = Boolean(age) && button.dataset.growthAge === age;
      button.hidden = !compatible;
      button.disabled = !compatible;
      if (!compatible && button.getAttribute('aria-pressed') === 'true') {
        button.setAttribute('aria-pressed', 'false');
        invalidated = true;
      }
    });
    if (invalidated) this.wizard.markResultStale();
    this.wizard.syncControls();
  }

  syncVariant(variantId) {
    this.variant = variantId == null || variantId === '' ? undefined : this.variants.get(String(variantId));
  }

  handleCalculate = (event) => {
    if (event.target !== this.wizard || event.detail?.calculatorType !== 'cuccioli_mamme') return;
    this.lastValues = event.detail.values;
    this.render(this.lastValues);
  };

  render(values) {
    const selectedWeight = this.weightButtons.find((button) => (
      !button.hidden && button.dataset.growthAge === values?.age && button.dataset.wizardValue === values?.weight
    ));
    const dose = this.toPositiveNumber(selectedWeight?.dataset.growthDose);
    if (!selectedWeight || !dose) {
      this.wizard.showResult({ note: 'Dati di dosaggio non disponibili per la selezione.' });
      return;
    }

    const packageWeight = this.toPositiveNumber(this.variant?.package_weight_g);
    const priceCents = this.toPositiveNumber(this.variant?.price);
    let duration;
    let cost;
    let note;
    if (packageWeight) {
      const days = packageWeight / dose;
      if (Number.isFinite(days) && days > 0) {
        duration = `Il sacco da ${this.formatNumber(packageWeight / 1000, 2)} kg dura circa ${Math.max(1, Math.round(days))} giorni`;
        const dailyCost = priceCents > 0 ? (priceCents / 100) / days : 0;
        if (Number.isFinite(dailyCost) && dailyCost > 0) cost = `${this.formatCurrency(dailyCost)} al giorno`;
      }
    } else {
      note = 'Formato selezionato senza peso confezione disponibile.';
    }

    this.wizard.showResult({ dose: `${this.formatNumber(dose)} g / giorno`, duration, cost, note });
    this.wizard.dataset.resultStale = 'false';
  }

  handleProductSelect = (event) => {
    if (event.target instanceof Element && event.target.closest('product-card')) return;
    const immediateVariantId = event.detail?.variantId;
    if (immediateVariantId != null && this.variants.has(String(immediateVariantId))) this.updateVariant(immediateVariantId);
    const promise = event.promise;
    if (!promise || typeof promise.then !== 'function') return;
    promise.then(({ detail }) => {
      if (detail?.productId != null && String(detail.productId) !== String(this.dataset.productId)) return;
      const variantId = detail?.resource?.id ?? detail?.variant?.id ?? detail?.id;
      this.updateVariant(variantId);
    }).catch(() => {});
  };

  updateVariant(variantId) {
    this.syncVariant(variantId);
    if (variantId != null) {
      this.dataset.selectedVariantId = String(variantId);
      this.wizard.dataset.selectedVariantId = String(variantId);
    }
    const result = this.wizard.querySelector('[data-wizard-result]');
    if (this.lastValues && result && !result.hidden) this.render(this.lastValues);
  }

  toPositiveNumber(value) {
    if (value == null || value === '') return 0;
    const number = Number(typeof value === 'string' ? value.replace(',', '.').trim() : value);
    return Number.isFinite(number) && number > 0 ? number : 0;
  }

  formatNumber(value, maximumFractionDigits = 0) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, { maximumFractionDigits }).format(value);
    } catch {
      return String(value);
    }
  }

  formatCurrency(value) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, { style: 'currency', currency: this.dataset.currency || 'EUR' }).format(value);
    } catch {
      return `€${Number(value).toFixed(2)}`;
    }
  }
}

if (!customElements.get('cg-wizard-cuccioli-mamme-engine')) {
  customElements.define('cg-wizard-cuccioli-mamme-engine', CgWizardCuccioliMammeEngine);
}
